import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import CloseButton from '../../common/CloseButton/CloseButton';
import '../../common/CommonText.css';
import InputBoxSmall from '../../common/InputBox/InputBoxSmall';
import { showAlert } from '../../common/ExportLabel/Exporterror';

const AddUserRolePopup = ({
  isOpen,
  title,
  buttonName,
  fields,
  onSave,
  onClose,
  initialTableRows,
  popupErrorMessage
}) => {
  const [tableRows, setTableRows] = useState(initialTableRows);
  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    setTableRows(initialTableRows);
  }, [initialTableRows]);

  const handleInputChange = (field, value) => {
    setFormValues(prevValues => ({
      ...prevValues,
      [field]: value,
    }));

    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleCheckboxToggle = (featureName, permissionId) => {
    setFormValues(prevValues => ({
      ...prevValues,
      permissions: {
        ...prevValues.permissions,
        [permissionId]: !prevValues.permissions?.[permissionId]
      }
    }));
  };

  const validateField = (field) => {
    const validators = field.validators || [];
    const value = formValues[field.name] || "";

    for (const validator of validators) {
      const result = validator(value);
      if (typeof result === "string") {
        return result;
      }
    }

    return null;
  };

  const handleSubmit = () => {
    const errors = {};
    let hasErrors = false;
  
    fields.forEach(field => {
      const error = validateField(field);
      if (error) {
        errors[field.name] = error;
        hasErrors = true;
      }
    });
  
    const selectedPermissions = Object.entries(formValues.permissions || {})
      .filter(([, isChecked]) => isChecked)
      .map(([id]) => parseInt(id, 10));

    if (selectedPermissions.length === 0) {
      showAlert("Please select at least one permission.");
      return;
    }
  
    setFormErrors(errors);
  
    if (hasErrors) return;
  
    const formData = {
      ...formValues,
      permissions: selectedPermissions,
    };
  
    onSave(formData);
    setFormValues({});
    setFormErrors({});
  };

  const handleClose = () => {

    setFormErrors({});
    if (onClose) onClose();
  };

  const renderErrorMessage = (fieldName) => {
    if (!formErrors[fieldName]) return null;
    return (
      <div className="validation-error" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
        {formErrors[fieldName]}
      </div>
    );
  };

  if (!isOpen) return null;
  const hasTableData = tableRows && tableRows.length > 0;

  return (
    <div className="popup-overlay">
      <div className="popup add-popup popup1 internal-user-popup">
        <CloseButton onClick={handleClose} />
        {popupErrorMessage && (
          <div className="popup-error-message fts-13" style={{ color: 'red', marginBottom: '10px' }}>
            {popupErrorMessage}
          </div>
        )}
        <div className="popup-name-button">
          <h2 className="popup-name">{title}</h2>
          <button className="popup-add-button common-text white fts-15 ftw-5" onClick={handleSubmit}>
            {buttonName}
          </button>
        </div>
        <div className="add-input-box-container">
          {fields.map((field, index) => {
            if (field.type === 'small-input') {
              return (
                <div key={index} className="field-container w-50">
                  <InputBoxSmall
                    {...field}
                    value={formValues[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    error= {formErrors[field.name]}
                  />
                </div>
              );
            }
            return null;
          })}
        </div>

        {hasTableData && (
          <div className="internal-user-table-container">
            <div className="internal-user-table-scrollable">
              <table className="internal-user-table">
                <thead className="common-table-head internal-user-table-thead">
                  <tr className="common-table-thead-tr internal-user-role-tr">
                    <th className="text-left">Permission</th>
                    <th className="text-center">View</th>
                    <th className="text-center">View All</th>
                    <th className="text-center">Add</th>
                    <th className="text-center">Edit</th>
                    <th className="text-center">Archive</th>
                    <th className="text-center">Delete</th>
                  </tr>
                </thead>
                <tbody className="internal-user-table-tbody fts-15">
                  {tableRows.map((row, rowIndex) => (
                    <tr key={rowIndex} className="internal-user-table-tbody-tr">
                      <td>
                        {row.feature_name
                          .replace(/_/g, ' ')
                          .split(' ')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ')}
                      </td>
                      {['view','view_all' ,'add', 'edit', 'archive', 'delete'].map((accessName, colIndex) => {
                        const permission = row.permissions.find(perm => perm.access_name === accessName);
                        return (
                          <td key={colIndex} className="text-center">
                            {permission ? (
                              <input
                                className="table-selectbox internal-user-table-checkbox"
                                type="checkbox"
                                checked={formValues.permissions?.[permission.id] || false}
                                onChange={() => handleCheckboxToggle(row.feature_name, permission.id)}
                              />
                            ) : (
                              <span>&nbsp;</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

AddUserRolePopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  buttonName: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  initialTableRows: PropTypes.arrayOf(
    PropTypes.shape({
      feature_name: PropTypes.string.isRequired,
      permissions: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          access_name: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  popupErrorMessage: PropTypes.string,
};

export default AddUserRolePopup;